import React, { useState, useEffect } from 'react';
import { Table, Card, DatePicker, Button, Space, Tag, message, Select, Alert, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faPhone, faTags, faTrash } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import axios from 'axios';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Preset date ranges
const datePresets = [
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 14 Days', value: 14 },
  { label: 'Last 30 Days', value: 30 },
  { label: 'Last 3 Months', value: 90 },
  { label: 'Last 6 Months', value: 180 }
];

const RingCentralCallsTable = () => {
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [assigningCampaign, setAssigningCampaign] = useState(false);
  const [deletingCalls, setDeletingCalls] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(90); // Default to 3 months
  const [dateRange, setDateRange] = useState([
    moment().subtract(90, 'days'),
    moment()
  ]);

  useEffect(() => {
    loadCalls();
    loadCampaigns();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;
      
      const response = await axios.get('http://localhost:5000/api/call-analytics/ringcentral-calls', {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        }
      });
      
      setCalls(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error loading RingCentral calls:', error);
      message.error('Failed to load call data');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/call-analytics/available-campaigns');
      setCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      message.error('Failed to load campaigns');
    }
  };

  const handlePresetChange = (days) => {
    setSelectedPreset(days);
    setDateRange([
      moment().subtract(days, 'days'),
      moment()
    ]);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select calls to delete');
      return;
    }

    try {
      setDeletingCalls(true);
      await axios.post('http://localhost:5000/api/call-analytics/bulk-delete-calls', {
        callIds: selectedRowKeys
      });
      
      message.success(`Deleted ${selectedRowKeys.length} calls`);
      setSelectedRowKeys([]);
      loadCalls(); // Reload to show updated list
    } catch (error) {
      console.error('Error deleting calls:', error);
      message.error('Failed to delete calls');
    } finally {
      setDeletingCalls(false);
    }
  };

  const handleBulkAssignCampaign = async (campaignName) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select calls to assign');
      return;
    }

    try {
      setAssigningCampaign(true);
      await axios.post('http://localhost:5000/api/call-analytics/bulk-assign-campaign', {
        callIds: selectedRowKeys,
        campaignName
      });
      
      message.success(`Assigned ${selectedRowKeys.length} calls to "${campaignName}"`);
      setSelectedRowKeys([]);
      loadCalls(); // Reload to show updated campaign assignments
    } catch (error) {
      console.error('Error assigning campaign:', error);
      message.error('Failed to assign campaign');
    } finally {
      setAssigningCampaign(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Time',
      dataIndex: 'date',
      key: 'time',
      width: 100,
      render: (date) => moment(date).format('h:mm A')
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      render: (direction) => (
        <Tag color={direction === 'Incoming' ? 'blue' : 'green'}>
          {direction}
        </Tag>
      )
    },
    {
      title: 'From',
      dataIndex: 'fromNumber',
      key: 'fromNumber',
      width: 140
    },
    {
      title: 'To',
      dataIndex: 'toNumber',
      key: 'toNumber',
      width: 140
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      align: 'right',
      render: (duration) => {
        if (!duration) return '-';
        // Duration is in seconds
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
    },
    {
      title: 'Result',
      dataIndex: 'actionResult',
      key: 'actionResult',
      width: 120,
      render: (result) => {
        const colorMap = {
          'Accepted': 'success',
          'Missed': 'error',
          'Voicemail': 'warning',
          'Rejected': 'error',
          'Busy': 'orange'
        };
        return (
          <Tag color={colorMap[result] || 'default'}>
            {result || 'Unknown'}
          </Tag>
        );
      }
    },
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      width: 150,
      render: (campaign) => campaign || <Tag>Not Mapped</Tag>
    },
    {
      title: 'Name',
      dataIndex: 'callerName',
      key: 'callerName',
      width: 150,
      ellipsis: true
    }
  ];

  // Calculate statistics
  const stats = {
    total: calls.length,
    accepted: calls.filter(c => c.actionResult === 'Accepted').length,
    missed: calls.filter(c => c.actionResult === 'Missed').length,
    avgDuration: calls.reduce((sum, c) => sum + (c.duration || 0), 0) / (calls.length || 1)
  };

  return (
    <Card 
      title={
        <Space>
          <FontAwesomeIcon icon={faPhone} />
          <span>RingCentral Call Logs</span>
          <Tag color="blue">{stats.total} total</Tag>
          <Tag color="green">{stats.accepted} accepted</Tag>
          <Tag color="red">{stats.missed} missed</Tag>
          <Tag color="purple">Avg: {Math.round(stats.avgDuration)}s</Tag>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={selectedPreset}
            onChange={handlePresetChange}
            style={{ width: 150 }}
            placeholder="Select period"
          >
            {datePresets.map(preset => (
              <Option key={preset.value} value={preset.value}>
                {preset.label}
              </Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                setDateRange(dates);
                setSelectedPreset(null); // Clear preset when manually selecting dates
              }
            }}
            format="MMM DD, YYYY"
            allowClear={false}
            disabledDate={(current) => {
              // Disable future dates
              return current && current > moment().endOf('day');
            }}
            presets={[
              { label: 'Last 7 Days', value: [moment().subtract(7, 'days'), moment()] },
              { label: 'Last 30 Days', value: [moment().subtract(30, 'days'), moment()] },
              { label: 'Last 3 Months', value: [moment().subtract(90, 'days'), moment()] }
            ]}
          />
          <Button 
            icon={<FontAwesomeIcon icon={faSync} />} 
            onClick={loadCalls}
          >
            Refresh
          </Button>
        </Space>
      }
    >
      {selectedRowKeys.length > 0 && (
        <Alert
          message={
            <Space>
              <span><strong>{selectedRowKeys.length}</strong> calls selected</span>
              <Select
                placeholder="Assign to Campaign"
                style={{ width: 250 }}
                onChange={handleBulkAssignCampaign}
                loading={assigningCampaign}
                suffixIcon={<FontAwesomeIcon icon={faTags} />}
              >
                {campaigns.map(c => (
                  <Option key={c.campaignName} value={c.campaignName}>
                    {c.campaignName} ({c.totalCalls} calls)
                  </Option>
                ))}
              </Select>
              <Popconfirm
                title="Delete selected calls?"
                description={`Are you sure you want to delete ${selectedRowKeys.length} calls? This cannot be undone.`}
                onConfirm={handleBulkDelete}
                okText="Yes, delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button 
                  danger
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  loading={deletingCalls}
                >
                  Delete Selected
                </Button>
              </Popconfirm>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              type="text"
              onClick={() => {
                setSelectedRowKeys([]);
                message.info('Selection cleared');
              }}
            >
              Clear Selection
            </Button>
          }
        />
      )}
      
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={calls}
        loading={loading}
        rowKey="_id"
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} calls`,
          pageSizeOptions: ['20', '50', '100', '200']
        }}
        size="small"
        scroll={{ x: 1200 }}
      />
    </Card>
  );
};

export default RingCentralCallsTable;
